import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  Check,
  ArrowRight,
  Store,
  UserCircle,
  Package,
  Settings,
  BookOpen,
  MessageCircle,
  HelpCircle,
  CreditCard,
  BarChart3,
  Tag,
  ListTodo,
  Users,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Card as BaseCard } from "../components/ui/Card";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchStores, createStore } from "../store/actions/storeActions";
import { updateMeProfile } from "../store/actions/authActions";
import { fetchProducts, createProduct } from "../store/actions/productActions";
import { fetchPlan } from "../store/actions/billingActions";
import { fetchMe } from "../store/actions/authActions";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { fetchWooCommerceProducts, fetchShopifyProducts, importProducts } from "../services/integrationServices";
import { createInvite } from "../services/inviteServices";
import { Copy, ChevronLeft, ChevronRight } from "lucide-react";

const Container = styled.div`
  max-width: 1040px;
  margin: 0 auto;
`;

const Intro = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const H1 = styled.h1`
  margin: 0 0 12px;
  font-size: 30px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const P = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.body};
  max-width: 640px;
  margin-inline: auto;
`;

const ProgressWrap = styled.div`
  max-width: 420px;
  margin: 24px auto 0;
`;

const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 9999px;
  background: #e5e7eb;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #9b87f5, #7e69ab);
  width: ${({ $value }) => `${$value}%`};
  transition: width 0.3s ease;
`;

const TabsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-radius: 9999px;
  background: #f3f4f6;
  padding: 4px;
  margin-bottom: 24px;
`;

const Tab = styled.button`
  border: none;
  background: ${({ $active }) => ($active ? "white" : "transparent")};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary.DEFAULT : theme.colors.text.body};
  font-size: 14px;
  font-weight: 600;
  padding: 8px 12px;
  border-radius: 9999px;
  cursor: pointer;
`;

const Card = styled(BaseCard)`
  padding: 20px 20px 18px;
  border: 1px solid rgba(155, 135, 245, 0.25);
  margin-bottom: 16px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const CardTitle = styled.div`
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CardDesc = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.body};
`;

const IconBubble = styled.div`
  padding: 8px;
  border-radius: 9999px;
  background: ${({ $completed }) => ($completed ? "#dcfce7" : "rgba(155,135,245,0.1)")};
  color: ${({ $completed }) => ($completed ? "#16a34a" : "#9b87f5")};
`;

function Step({ title, subtitle, description, icon, buttonText, buttonLink, completed, onButtonClick }) {
  const navigate = useNavigate();
  const Icon = icon;
  const handleClick = () => (onButtonClick ? onButtonClick() : navigate(buttonLink));
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <IconBubble $completed={completed}>
            <Icon size={18} />
          </IconBubble>
          <span>{title}{subtitle != null && <span style={{ fontWeight: 500, color: "#6b7280", fontSize: 14 }}> — {subtitle}</span>}</span>
        </CardTitle>
        {completed && <Check size={18} color="#22c55e" />}
      </CardHeader>
      <CardDesc>{description}</CardDesc>
      <Button
        $variant={completed ? "outline" : "solid"}
        onClick={handleClick}
      >
        {buttonText}
        <ArrowRight size={16} />
      </Button>
    </Card>
  );
}

export default function GetStarted() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("setup");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { stores = [], loading: storesLoading } = useSelector((state) => state.store);
  const { products = [], loading: productsLoading } = useSelector((state) => state.product);
  const billing = useSelector((state) => state.billing) || {};
  const {
    plan: billingPlan,
    planName,
    features: planFeatures = [],
    limits: planLimits,
    trialEndsAt,
    isTrialing,
  } = billing;

  const [storeName, setStoreName] = useState("");
  const [storeUrl, setStoreUrl] = useState("");
  const [storePlatform, setStorePlatform] = useState("woocommerce");
  const [storeSubmitting, setStoreSubmitting] = useState(false);
  const [productName, setProductName] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [productSku, setProductSku] = useState("");
  const [productStoreId, setProductStoreId] = useState("");
  const [productSubmitting, setProductSubmitting] = useState(false);

  // Store Modal State
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [storeModalName, setStoreModalName] = useState("");
  const [storeModalUrl, setStoreModalUrl] = useState("");
  const [storeModalPlatform, setStoreModalPlatform] = useState("woocommerce");
  const [storeModalLocation, setStoreModalLocation] = useState("");
  const [storeModalAdmin, setStoreModalAdmin] = useState("");
  const [storeModalConsumerKey, setStoreModalConsumerKey] = useState("");
  const [storeModalConsumerSecret, setStoreModalConsumerSecret] = useState("");
  const [storeModalAccessToken, setStoreModalAccessToken] = useState("");
  const [storeModalSubmitting, setStoreModalSubmitting] = useState(false);
  const [storeModalError, setStoreModalError] = useState(null);

  // Product Import Wizard State
  const [isProductWizardOpen, setIsProductWizardOpen] = useState(false);
  const [productWizardStep, setProductWizardStep] = useState("source"); // "source" | "select" | "confirm" | "manual"
  const [productImportSource, setProductImportSource] = useState(null); // "woocommerce" | "shopify" | "manual"
  const [selectedStoreForImport, setSelectedStoreForImport] = useState("");
  const [availableRemoteProducts, setAvailableRemoteProducts] = useState([]);
  const [selectedRemoteProductIds, setSelectedRemoteProductIds] = useState([]);
  const [productWizardSubmitting, setProductWizardSubmitting] = useState(false);
  const [productWizardError, setProductWizardError] = useState(null);
  const [loadingRemoteProducts, setLoadingRemoteProducts] = useState(false);
  const [pendingPreselectedStoreId, setPendingPreselectedStoreId] = useState(null);

  // Profile Modal State
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileFullName, setProfileFullName] = useState("");
  const [profileBusinessName, setProfileBusinessName] = useState("");
  const [profileLogoUrl, setProfileLogoUrl] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileJobTitle, setProfileJobTitle] = useState("");
  const [profileTimezone, setProfileTimezone] = useState("UTC");
  const [profileLanguage, setProfileLanguage] = useState("en");
  const [profileCompanyWebsite, setProfileCompanyWebsite] = useState("");
  const [profileIndustry, setProfileIndustry] = useState("");
  const [profileCompanySize, setProfileCompanySize] = useState("");
  const [profileCurrency, setProfileCurrency] = useState("USD");
  const [profileAddressStreet, setProfileAddressStreet] = useState("");
  const [profileAddressCity, setProfileAddressCity] = useState("");
  const [profileAddressState, setProfileAddressState] = useState("");
  const [profileAddressCountry, setProfileAddressCountry] = useState("");
  const [profileAddressPostalCode, setProfileAddressPostalCode] = useState("");
  const [profileStep, setProfileStep] = useState(1);
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [profileError, setProfileError] = useState(null);

  // Invite Modal State
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [generatedInviteUrl, setGeneratedInviteUrl] = useState(null);

  useEffect(() => {
    dispatch(fetchMe());
    dispatch(fetchStores());
    dispatch(fetchProducts());
    dispatch(fetchPlan());
  }, [dispatch]);

  useEffect(() => {
    if (location.state?.openProductWizard) {
      setPendingPreselectedStoreId(location.state?.preselectedStoreId || null);
      setIsProductWizardOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openProductWizard, location.state?.preselectedStoreId, navigate, location.pathname]);

  useEffect(() => {
    if (pendingPreselectedStoreId && stores.length > 0) {
      const store = stores.find((s) => s._id === pendingPreselectedStoreId);
      if (store) {
        setSelectedStoreForImport(store._id);
        setProductImportSource(store.platform || "woocommerce");
      }
      setPendingPreselectedStoreId(null);
    }
  }, [pendingPreselectedStoreId, stores]);

  // Pre-fill profile modal when user data is available
  useEffect(() => {
    if (user && isProfileModalOpen) {
      setProfileFullName(user.fullName || "");
      setProfileBusinessName(user.businessName || "");
      setProfileLogoUrl(user.avatar || "");
      setProfilePhone(user.phone || "");
      setProfileJobTitle(user.jobTitle || "");
      setProfileTimezone(user.timezone || "UTC");
      setProfileLanguage(user.language || "en");
      setProfileCompanyWebsite(user.companyWebsite || "");
      setProfileIndustry(user.industry || "");
      setProfileCompanySize(user.companySize || "");
      setProfileCurrency(user.currency || "USD");
      setProfileAddressStreet(user.addressStreet || "");
      setProfileAddressCity(user.addressCity || "");
      setProfileAddressState(user.addressState || "");
      setProfileAddressCountry(user.addressCountry || "");
      setProfileAddressPostalCode(user.addressPostalCode || "");
    }
  }, [user, isProfileModalOpen]);

  const completedSteps = {
    connectStore: Array.isArray(stores) && stores.length > 0,
    setupProfile: !!(user?.fullName || user?.name),
    addProducts: Array.isArray(products) && products.length > 0,
    inviteTeam: false,
  };
  const progressPercent =
    (Object.values(completedSteps).filter(Boolean).length /
      Object.values(completedSteps).length) *
    100;

  const handleAddStore = async (e) => {
    e.preventDefault();
    const name = storeName.trim();
    const url = storeUrl.trim();
    if (!name || !url) return;
    setStoreSubmitting(true);
    try {
      await dispatch(createStore({ name, url, platform: storePlatform }));
      dispatch(fetchStores());
      setStoreName("");
      setStoreUrl("");
    } catch (err) {
      console.warn("Create store failed:", err);
    } finally {
      setStoreSubmitting(false);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    const name = productName.trim();
    const price = parseFloat(productPrice);
    const storeId = productStoreId || (stores[0] && stores[0]._id);
    if (!name || isNaN(price) || price < 0 || !storeId) return;
    setProductSubmitting(true);
    try {
      await dispatch(createProduct({ name, price, storeId, sku: productSku.trim() || undefined }));
      dispatch(fetchProducts());
      setProductName("");
      setProductPrice("");
      setProductSku("");
    } catch (err) {
      console.warn("Create product failed:", err);
    } finally {
      setProductSubmitting(false);
    }
  };

  // Store Modal Handlers
  const handleOpenStoreModal = () => {
    setIsStoreModalOpen(true);
    setStoreModalError(null);
  };

  const handleCloseStoreModal = () => {
    setIsStoreModalOpen(false);
    setStoreModalName("");
    setStoreModalUrl("");
    setStoreModalPlatform("woocommerce");
    setStoreModalLocation("");
    setStoreModalAdmin("");
    setStoreModalConsumerKey("");
    setStoreModalConsumerSecret("");
    setStoreModalAccessToken("");
    setStoreModalError(null);
  };

  const handleSubmitStoreModal = async (e) => {
    e.preventDefault();
    const name = storeModalName.trim();
    const url = storeModalUrl.trim();
    const platform = storeModalPlatform;
    if (!name || !url) {
      setStoreModalError(t("getStarted.storeNameAndUrlRequired"));
      return;
    }
    if (platform === "woocommerce") {
      const ck = storeModalConsumerKey.trim();
      const cs = storeModalConsumerSecret.trim();
      if (!ck || !cs) {
        setStoreModalError(t("getStarted.consumerKeySecretRequired"));
        return;
      }
    }
    if (platform === "shopify") {
      const token = storeModalAccessToken.trim();
      if (!token) {
        setStoreModalError(t("getStarted.accessTokenRequiredShopify"));
        return;
      }
    }
    setStoreModalSubmitting(true);
    setStoreModalError(null);
    try {
      const credentials = platform === "woocommerce"
        ? { consumerKey: storeModalConsumerKey.trim(), consumerSecret: storeModalConsumerSecret.trim() }
        : { accessToken: storeModalAccessToken.trim() };
      await dispatch(createStore({
        name,
        url,
        platform,
        credentials,
        admin: storeModalAdmin.trim() || undefined,
        location: storeModalLocation.trim() || undefined,
      }));
      dispatch(fetchStores());
      handleCloseStoreModal();
    } catch (err) {
      setStoreModalError(err.message || t("getStarted.failedToCreateStore"));
    } finally {
      setStoreModalSubmitting(false);
    }
  };

  // Product Import Wizard Handlers
  const handleOpenProductWizard = () => {
    setIsProductWizardOpen(true);
    setProductWizardStep("source");
    setProductImportSource(null);
    setSelectedStoreForImport("");
    setAvailableRemoteProducts([]);
    setSelectedRemoteProductIds([]);
    setProductWizardError(null);
  };

  const handleCloseProductWizard = () => {
    setIsProductWizardOpen(false);
    setProductWizardStep("source");
    setProductImportSource(null);
    setSelectedStoreForImport("");
    setAvailableRemoteProducts([]);
    setSelectedRemoteProductIds([]);
    setProductWizardError(null);
  };

  const handleProductWizardNext = async () => {
    if (productWizardStep === "source") {
      if (!productImportSource) {
        setProductWizardError(t("getStarted.pleaseSelectImportSource"));
        return;
      }
      if (productImportSource === "manual") {
        setProductWizardStep("manual");
      } else {
        if (!selectedStoreForImport) {
          setProductWizardError(t("getStarted.pleaseSelectStore"));
          return;
        }
        setProductWizardStep("select");
        setLoadingRemoteProducts(true);
        setProductWizardError(null);
        try {
          const products = productImportSource === "woocommerce"
            ? await fetchWooCommerceProducts(selectedStoreForImport)
            : await fetchShopifyProducts(selectedStoreForImport);
          setAvailableRemoteProducts(Array.isArray(products) ? products : []);
          setSelectedRemoteProductIds([]);
        } catch (err) {
          setProductWizardError(err.message || t("getStarted.failedToFetchProducts"));
        } finally {
          setLoadingRemoteProducts(false);
        }
      }
    } else if (productWizardStep === "select") {
      if (selectedRemoteProductIds.length === 0) {
        setProductWizardError(t("getStarted.pleaseSelectOneProduct"));
        return;
      }
      setProductWizardStep("confirm");
    } else if (productWizardStep === "confirm") {
      setProductWizardSubmitting(true);
      setProductWizardError(null);
      try {
        const selectedProducts = availableRemoteProducts.filter((p) =>
          selectedRemoteProductIds.includes(p.externalId)
        );
        await importProducts({
          storeId: selectedStoreForImport,
          source: productImportSource,
          products: selectedProducts,
        });
        dispatch(fetchProducts());
        handleCloseProductWizard();
      } catch (err) {
        setProductWizardError(err.message || t("getStarted.failedToImportProducts"));
      } finally {
        setProductWizardSubmitting(false);
      }
    }
  };

  const handleProductWizardBack = () => {
    if (productWizardStep === "select" || productWizardStep === "manual") {
      setProductWizardStep("source");
      setSelectedRemoteProductIds([]);
      setAvailableRemoteProducts([]);
    } else if (productWizardStep === "confirm") {
      setProductWizardStep("select");
    }
    setProductWizardError(null);
  };

  const toggleRemoteProductSelection = (externalId) => {
    setSelectedRemoteProductIds((prev) =>
      prev.includes(externalId)
        ? prev.filter((id) => id !== externalId)
        : [...prev, externalId]
    );
  };

  // Profile Modal Handlers
  const handleOpenProfileModal = () => {
    setIsProfileModalOpen(true);
    setProfileError(null);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
    setProfileStep(1);
    setProfileError(null);
  };

  const handleSubmitProfileModal = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!profileFullName.trim()) {
      setProfileError(t("getStarted.fullNameRequired"));
      return;
    }
    setProfileSubmitting(true);
    setProfileError(null);
    try {
      const result = await dispatch(updateMeProfile({
        fullName: profileFullName.trim(),
        avatar: profileLogoUrl.trim() || undefined,
        businessName: profileBusinessName.trim() || undefined,
        phone: profilePhone.trim() || undefined,
        jobTitle: profileJobTitle.trim() || undefined,
        timezone: profileTimezone || undefined,
        language: profileLanguage || undefined,
        companyWebsite: profileCompanyWebsite.trim() || undefined,
        industry: profileIndustry.trim() || undefined,
        companySize: profileCompanySize || undefined,
        currency: profileCurrency || undefined,
        addressStreet: profileAddressStreet.trim() || undefined,
        addressCity: profileAddressCity.trim() || undefined,
        addressState: profileAddressState.trim() || undefined,
        addressCountry: profileAddressCountry.trim() || undefined,
        addressPostalCode: profileAddressPostalCode.trim() || undefined,
      }));
      if (result?.success) handleCloseProfileModal();
      else setProfileError(result?.error || t("getStarted.failedToUpdateProfile"));
    } catch (err) {
      setProfileError(err.message || t("getStarted.failedToUpdateProfile"));
    } finally {
      setProfileSubmitting(false);
    }
  };

  // Invite Modal Handlers
  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
    setInviteError(null);
    setGeneratedInviteUrl(null);
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
    setInviteEmail("");
    setInviteRole("user");
    setInviteMessage("");
    setInviteError(null);
    setGeneratedInviteUrl(null);
  };

  const handleSubmitInviteModal = async (e) => {
    e.preventDefault();
    if (!inviteRole) {
      setInviteError(t("getStarted.roleRequiredError"));
      return;
    }
    setInviteSubmitting(true);
    setInviteError(null);
    try {
      const result = await createInvite({
        email: inviteEmail.trim() || undefined,
        role: inviteRole,
        message: inviteMessage.trim() || undefined,
      });
      setGeneratedInviteUrl(result.inviteUrl);
    } catch (err) {
      setInviteError(err.response?.data?.message || err.message || t("getStarted.failedToCreateInvite"));
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleCopyInviteUrl = () => {
    if (generatedInviteUrl) {
      navigator.clipboard.writeText(generatedInviteUrl);
      // Could show a toast here
    }
  };

  return (
    <Container className="animate-fadeIn">
      <Intro>
        <H1>{user?.fullName ? t("getStarted.welcomeToName", { name: user.fullName }) : t("getStarted.welcomeTo")}</H1>
        <P>{t("getStarted.introParagraph")}</P>

        <ProgressWrap>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 13,
              marginBottom: 6,
            }}
          >
            <span>{t("getStarted.setupProgress")}</span>
            <span>{t("getStarted.percentComplete", { percent: Math.round(progressPercent) })}</span>
          </div>
          <ProgressTrack>
            <ProgressFill $value={progressPercent} />
          </ProgressTrack>
        </ProgressWrap>
      </Intro>

      <TabsBar>
        <Tab $active={activeTab === "setup"} onClick={() => setActiveTab("setup")}>
          {t("getStarted.initialSetup")}
        </Tab>
        <Tab
          $active={activeTab === "advanced"}
          onClick={() => setActiveTab("advanced")}
        >
          {t("getStarted.advancedFeatures")}
        </Tab>
        <Tab
          $active={activeTab === "resources"}
          onClick={() => setActiveTab("resources")}
        >
          {t("getStarted.resources")}
        </Tab>
      </TabsBar>

      {activeTab === "setup" && (
        <div>
          <Step
            title={t("getStarted.connectYourStore")}
            subtitle={stores.length === 0 ? t("getStarted.noStoreYet") : t("getStarted.storesConnected", { count: stores.length })}
            description={t("getStarted.connectStoreDescription")}
            icon={Store}
            buttonText={t("getStarted.connectStore")}
            onButtonClick={handleOpenStoreModal}
            completed={completedSteps.connectStore}
          >
            {!completedSteps.connectStore && (
              <form onSubmit={handleAddStore} style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 12, maxWidth: 400 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#374151" }}>{t("getStarted.quickAddFirstStore")}</p>
                <Input placeholder={t("getStarted.storeName")} value={storeName} onChange={(e) => setStoreName(e.target.value)} required />
                <Input placeholder={t("getStarted.storeUrlPlaceholder")} type="url" value={storeUrl} onChange={(e) => setStoreUrl(e.target.value)} required />
                <select value={storePlatform} onChange={(e) => setStorePlatform(e.target.value)} style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}>
                  <option value="woocommerce">{t("getStarted.woocommerce")}</option>
                  <option value="shopify">{t("getStarted.shopify")}</option>
                </select>
                <Button type="submit" disabled={storeSubmitting || storesLoading}>{storeSubmitting ? t("getStarted.adding") : t("getStarted.addStore")}</Button>
              </form>
            )}
          </Step>
          <Step
            title={t("getStarted.completeYourProfile")}
            subtitle={completedSteps.setupProfile ? t("getStarted.profileComplete") : t("getStarted.addYourName")}
            description={t("getStarted.completeProfileDescription")}
            icon={UserCircle}
            buttonText={t("getStarted.editProfile")}
            onButtonClick={handleOpenProfileModal}
            completed={completedSteps.setupProfile}
          />
          <Step
            title={t("getStarted.addYourProducts")}
            subtitle={products.length === 0 ? t("getStarted.noProductsYet") : t("getStarted.productsCount", { count: products.length })}
            description={t("getStarted.addProductsDescription")}
            icon={Package}
            buttonText={t("getStarted.addProducts")}
            onButtonClick={handleOpenProductWizard}
            completed={completedSteps.addProducts}
          >
            {!completedSteps.addProducts && (
              <>
                {stores.length === 0 ? (
                  <p style={{ margin: "0 0 16px", fontSize: 14, color: "#6b7280" }}>{t("getStarted.createStoreFirst")}</p>
                ) : (
                  <form onSubmit={handleAddProduct} style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 12, maxWidth: 400 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#374151" }}>{t("getStarted.quickAddFirstProduct")}</p>
                    <Input placeholder={t("getStarted.productName")} value={productName} onChange={(e) => setProductName(e.target.value)} required />
                    <Input placeholder={t("getStarted.price")} type="number" step="0.01" min="0" value={productPrice} onChange={(e) => setProductPrice(e.target.value)} required />
                    <Input placeholder={t("getStarted.skuOptional")} value={productSku} onChange={(e) => setProductSku(e.target.value)} />
                    {stores.length > 1 && (
                      <select value={productStoreId} onChange={(e) => setProductStoreId(e.target.value)} style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}>
                        <option value="">{t("getStarted.selectStore")}</option>
                        {stores.map((s) => (
                          <option key={s._id} value={s._id}>{s.name}</option>
                        ))}
                      </select>
                    )}
                    <Button type="submit" disabled={productSubmitting || productsLoading || !(productStoreId || stores[0]?._id)}>{productSubmitting ? t("getStarted.adding") : t("getStarted.addProduct")}</Button>
                  </form>
                )}
              </>
            )}
          </Step>
          <Step
            title={t("getStarted.inviteYourTeam")}
            subtitle={t("getStarted.availableOnPaidPlans")}
            description={t("getStarted.inviteTeamDescription")}
            icon={Settings}
            buttonText={t("getStarted.inviteTeam")}
            onButtonClick={handleOpenInviteModal}
            completed={completedSteps.inviteTeam}
          />
        </div>
      )}

      {activeTab === "advanced" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <p style={{ margin: "0 0 8px", color: "#6b7280", fontSize: 14 }}>
            {t("getStarted.advancedIntro")}
          </p>
          <Card>
            <CardHeader>
              <CardTitle>
                <IconBubble $completed={false}><ListTodo size={18} /></IconBubble>
                {t("getStarted.tasks")}
              </CardTitle>
            </CardHeader>
            <CardDesc>{t("getStarted.tasksDescription")}</CardDesc>
            <Button $variant="outline" onClick={() => navigate("/tasks")}>{t("getStarted.goToTasks")} <ArrowRight size={16} /></Button>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                <IconBubble $completed={false}><BarChart3 size={18} /></IconBubble>
                {t("getStarted.analytics")}
              </CardTitle>
            </CardHeader>
            <CardDesc>{t("getStarted.analyticsDescription")}</CardDesc>
            <Button $variant="outline" onClick={() => navigate("/analytics")}>{t("getStarted.goToAnalytics")} <ArrowRight size={16} /></Button>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                <IconBubble $completed={false}><Tag size={18} /></IconBubble>
                {t("getStarted.coupons")}
              </CardTitle>
            </CardHeader>
            <CardDesc>{t("getStarted.couponsDescription")}</CardDesc>
            <Button $variant="outline" onClick={() => navigate("/coupons")}>{t("getStarted.goToCoupons")} <ArrowRight size={16} /></Button>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                <IconBubble $completed={false}><MessageCircle size={18} /></IconBubble>
                {t("getStarted.support")}
              </CardTitle>
            </CardHeader>
            <CardDesc>{t("getStarted.supportDescription")}</CardDesc>
            <Button $variant="outline" onClick={() => navigate("/support")}>{t("getStarted.goToSupport")} <ArrowRight size={16} /></Button>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                <IconBubble $completed={false}><CreditCard size={18} /></IconBubble>
                {t("getStarted.billingPlans")}
              </CardTitle>
            </CardHeader>
            <CardDesc>{t("getStarted.billingDescription")}</CardDesc>
            <Button $variant="outline" onClick={() => navigate("/billing")}>{t("getStarted.goToBilling")} <ArrowRight size={16} /></Button>
          </Card>
        </div>
      )}

      {activeTab === "resources" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <p style={{ margin: "0 0 8px", color: "#6b7280", fontSize: 14 }}>
            {t("getStarted.resourcesIntro")}
          </p>
          <Card>
            <CardHeader>
              <CardTitle>
                <IconBubble $completed={false}><MessageCircle size={18} /></IconBubble>
                {t("getStarted.contactSupport")}
              </CardTitle>
            </CardHeader>
            <CardDesc>{t("getStarted.contactSupportDescription")}</CardDesc>
            <Button $variant="outline" onClick={() => navigate("/support")}><HelpCircle size={16} /> {t("getStarted.openSupport")}</Button>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                <IconBubble $completed={false}><CreditCard size={18} /></IconBubble>
                {t("getStarted.billingPlans")}
              </CardTitle>
            </CardHeader>
            <CardDesc>{t("getStarted.billingDescription")}</CardDesc>
            <Button $variant="outline" onClick={() => navigate("/billing")}>{t("getStarted.viewBilling")}</Button>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                <IconBubble $completed={false}><BookOpen size={18} /></IconBubble>
                {t("getStarted.helpDocumentation")}
              </CardTitle>
            </CardHeader>
            <CardDesc>{t("getStarted.helpDocumentationDescription")}</CardDesc>
            <Button $variant="outline" disabled><BookOpen size={16} /> {t("getStarted.comingSoon")}</Button>
          </Card>
        </div>
      )}

      {/* Store Modal */}
      <Modal
        open={isStoreModalOpen}
        onClose={handleCloseStoreModal}
        title={t("getStarted.connectYourStore")}
        maxWidth="520px"
      >
        <form onSubmit={handleSubmitStoreModal} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
              {t("getStarted.storeNameRequired")}
            </label>
            <Input
              placeholder={t("getStarted.storeNamePlaceholder")}
              value={storeModalName}
              onChange={(e) => setStoreModalName(e.target.value)}
              required
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
              {t("getStarted.storeUrlRequired")}
            </label>
            <Input
              placeholder={storeModalPlatform === "shopify" ? t("getStarted.storeUrlPlaceholderShopify") : t("getStarted.storeUrlPlaceholderWoo")}
              type="text"
              value={storeModalUrl}
              onChange={(e) => setStoreModalUrl(e.target.value)}
              required
            />
            {storeModalPlatform === "shopify" && (
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "#6b7280" }}>
                {t("getStarted.shopifyDomainHint")}
              </p>
            )}
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
              {t("getStarted.platformRequired")}
            </label>
            <select
              value={storeModalPlatform}
              onChange={(e) => setStoreModalPlatform(e.target.value)}
              style={{ width: "100%", padding: "12px 16px", borderRadius: 12, border: "1px solid #d1d5db", fontSize: 16 }}
            >
              <option value="woocommerce">{t("getStarted.woocommerce")}</option>
              <option value="shopify">{t("getStarted.shopify")}</option>
            </select>
          </div>

          {storeModalPlatform === "woocommerce" && (
            <>
              <div>
                <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                  {t("getStarted.consumerKeyRequired")}
                </label>
                <Input
                  placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={storeModalConsumerKey}
                  onChange={(e) => setStoreModalConsumerKey(e.target.value)}
                  type="text"
                  autoComplete="off"
                />
                <p style={{ margin: "6px 0 0", fontSize: 12, color: "#6b7280" }}>
                  {t("getStarted.consumerKeyHint")}
                </p>
              </div>
              <div>
                <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                  {t("getStarted.consumerSecretRequired")}
                </label>
                <Input
                  placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={storeModalConsumerSecret}
                  onChange={(e) => setStoreModalConsumerSecret(e.target.value)}
                  type="password"
                  autoComplete="off"
                />
              </div>
            </>
          )}

          {storeModalPlatform === "shopify" && (
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                {t("getStarted.accessTokenRequired")}
              </label>
              <Input
                placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={storeModalAccessToken}
                onChange={(e) => setStoreModalAccessToken(e.target.value)}
                type="password"
                autoComplete="off"
              />
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "#6b7280" }}>
                {t("getStarted.accessTokenHint")}
              </p>
            </div>
          )}

          <div>
            <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
              {t("getStarted.locationOptional")}
            </label>
            <Input
              placeholder={t("getStarted.locationPlaceholder")}
              value={storeModalLocation}
              onChange={(e) => setStoreModalLocation(e.target.value)}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
              {t("getStarted.adminContactOptional")}
            </label>
            <Input
              placeholder={t("getStarted.adminContactPlaceholder")}
              value={storeModalAdmin}
              onChange={(e) => setStoreModalAdmin(e.target.value)}
            />
          </div>
          {storeModalError && (
            <div style={{ padding: 12, background: "#fee2e2", borderRadius: 8, color: "#991b1b", fontSize: 14 }}>
              {storeModalError}
            </div>
          )}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button type="button" $variant="outline" onClick={handleCloseStoreModal}>
                {t("common.cancel")}
              </Button>
            <Button type="submit" disabled={storeModalSubmitting}>
              {storeModalSubmitting ? t("getStarted.connecting") : t("getStarted.connectStore")}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Product Import Wizard Modal */}
      <Modal
        open={isProductWizardOpen}
        onClose={handleCloseProductWizard}
        title={t("getStarted.addYourProducts")}
        maxWidth="960px"
      >
        {productWizardStep === "source" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>
              {t("getStarted.chooseHowToAdd")}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 12, padding: 16, border: "1px solid #d1d5db", borderRadius: 12, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="importSource"
                  value="woocommerce"
                  checked={productImportSource === "woocommerce"}
                  onChange={(e) => setProductImportSource(e.target.value)}
                />
                <span style={{ fontWeight: 600 }}>{t("getStarted.importFromWooCommerce")}</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 12, padding: 16, border: "1px solid #d1d5db", borderRadius: 12, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="importSource"
                  value="shopify"
                  checked={productImportSource === "shopify"}
                  onChange={(e) => setProductImportSource(e.target.value)}
                />
                <span style={{ fontWeight: 600 }}>{t("getStarted.importFromShopify")}</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 12, padding: 16, border: "1px solid #d1d5db", borderRadius: 12, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="importSource"
                  value="manual"
                  checked={productImportSource === "manual"}
                  onChange={(e) => setProductImportSource(e.target.value)}
                />
                <span style={{ fontWeight: 600 }}>{t("getStarted.addProductsManually")}</span>
              </label>
            </div>
            {(productImportSource === "woocommerce" || productImportSource === "shopify") && (
              <div>
                <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                  {t("getStarted.selectStoreRequired")}
                </label>
                <select
                  value={selectedStoreForImport}
                  onChange={(e) => setSelectedStoreForImport(e.target.value)}
                  style={{ width: "100%", padding: "12px 16px", borderRadius: 12, border: "1px solid #d1d5db", fontSize: 16 }}
                >
                  <option value="">{t("getStarted.selectAStore")}</option>
                  {stores
                    .filter((s) => s.platform === productImportSource)
                    .map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.name}
                      </option>
                    ))}
                </select>
              </div>
            )}
            {productWizardError && (
              <div style={{ padding: 12, background: "#fee2e2", borderRadius: 8, color: "#991b1b", fontSize: 14 }}>
                {productWizardError}
              </div>
            )}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <Button type="button" $variant="outline" onClick={handleCloseProductWizard}>
                {t("common.cancel")}
              </Button>
              <Button onClick={handleProductWizardNext}>
                {t("common.next")} <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}

        {productWizardStep === "select" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>
              {t("getStarted.selectProductsToImport")} ({t("getStarted.selectedCount", { count: selectedRemoteProductIds.length })})
            </p>
            {loadingRemoteProducts ? (
              <div style={{ textAlign: "center", padding: 40 }}>{t("getStarted.loadingProducts")}</div>
            ) : availableRemoteProducts.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>{t("getStarted.noProductsFound")}</div>
            ) : (
              <div style={{ maxHeight: 520, minHeight: 320, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16, padding: 16 }}>
                  {availableRemoteProducts.map((product) => {
                    const name = product.name || product.title || "Untitled";
                    const price = typeof product.price === "number" ? product.price : parseFloat(product.price) || 0;
                    const image = product.image || product.imageUrl || "";
                    const isSelected = selectedRemoteProductIds.includes(product.externalId);
                    return (
                      <label
                        key={product.externalId}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          border: isSelected ? "2px solid #6366f1" : "1px solid #e5e7eb",
                          borderRadius: 12,
                          overflow: "hidden",
                          cursor: "pointer",
                          background: isSelected ? "#eef2ff" : "#fff",
                          position: "relative",
                        }}
                      >
                        <div style={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRemoteProductSelection(product.externalId)}
                          />
                        </div>
                        <div style={{ aspectRatio: "1", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {image ? (
                            <img src={image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <span style={{ color: "#9ca3af", fontSize: 12 }}>{t("getStarted.noImage")}</span>
                          )}
                        </div>
                        <div style={{ padding: 12, flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{name}</div>
                          <div style={{ fontSize: 14, color: "#059669" }}>${Number(price).toFixed(2)}</div>
                          {product.sku && <div style={{ fontSize: 12, color: "#6b7280" }}>SKU: {product.sku}</div>}
                          {product.category && <div style={{ fontSize: 12, color: "#6b7280" }}>{product.category}</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {productWizardError && (
              <div style={{ padding: 12, background: "#fee2e2", borderRadius: 8, color: "#991b1b", fontSize: 14 }}>
                {productWizardError}
              </div>
            )}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <Button type="button" $variant="outline" onClick={handleProductWizardBack}>
                <ChevronLeft size={16} /> {t("common.back")}
              </Button>
              <Button onClick={handleProductWizardNext} disabled={selectedRemoteProductIds.length === 0}>
                {t("common.next")} <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}

        {productWizardStep === "confirm" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>
              {t("getStarted.confirmImportCount", { count: selectedRemoteProductIds.length })}
            </p>
            <div style={{ padding: 16, background: "#f3f4f6", borderRadius: 12 }}>
              <div style={{ fontSize: 14, color: "#374151" }}>
                <strong>{t("getStarted.source")}:</strong> {productImportSource === "woocommerce" ? t("getStarted.woocommerce") : t("getStarted.shopify")}
              </div>
              <div style={{ fontSize: 14, color: "#374151", marginTop: 8 }}>
                <strong>{t("getStarted.store")}:</strong> {stores.find((s) => s._id === selectedStoreForImport)?.name || "—"}
              </div>
            </div>
            {productWizardError && (
              <div style={{ padding: 12, background: "#fee2e2", borderRadius: 8, color: "#991b1b", fontSize: 14 }}>
                {productWizardError}
              </div>
            )}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <Button type="button" $variant="outline" onClick={handleProductWizardBack}>
                <ChevronLeft size={16} /> {t("common.back")}
              </Button>
              <Button onClick={handleProductWizardNext} disabled={productWizardSubmitting}>
                {productWizardSubmitting ? t("getStarted.importing") : t("getStarted.importProducts")}
              </Button>
            </div>
          </div>
        )}

        {productWizardStep === "manual" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <form onSubmit={handleAddProduct}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                    {t("getStarted.productNameRequired")}
                  </label>
                  <Input
                    placeholder={t("getStarted.productName")}
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                    {t("getStarted.priceRequired")}
                  </label>
                  <Input
                    placeholder="0.00"
                    type="number"
                    step="0.01"
                    min="0"
                    value={productPrice}
                    onChange={(e) => setProductPrice(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                    {t("getStarted.skuOptional")}
                  </label>
                  <Input
                    placeholder="SKU"
                    value={productSku}
                    onChange={(e) => setProductSku(e.target.value)}
                  />
                </div>
                {stores.length > 1 && (
                  <div>
                    <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                      {t("getStarted.store")} *
                    </label>
                    <select
                      value={productStoreId}
                      onChange={(e) => setProductStoreId(e.target.value)}
                      style={{ width: "100%", padding: "12px 16px", borderRadius: 12, border: "1px solid #d1d5db", fontSize: 16 }}
                      required
                    >
                      <option value="">{t("getStarted.selectStore")}</option>
                      {stores.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                  <Button type="button" $variant="outline" onClick={handleCloseProductWizard}>
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={productSubmitting}>
                    {productSubmitting ? t("getStarted.addingProduct") : t("getStarted.addProductButton")}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        )}
      </Modal>

      {/* Profile Modal - multi-step */}
      <Modal
        open={isProfileModalOpen}
        onClose={handleCloseProfileModal}
        title={t("getStarted.completeYourProfile")}
        maxWidth="720px"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>{t("getStarted.stepOf", { current: profileStep, total: 3 })}</span>
            <div style={{ flex: 1, height: 6, background: "#e5e7eb", borderRadius: 9999, overflow: "hidden" }}>
              <div style={{ width: `${(profileStep / 3) * 100}%`, height: "100%", background: "linear-gradient(90deg, #9b87f5, #7e69ab)", borderRadius: 9999, transition: "width 0.3s ease" }} />
            </div>
          </div>

          {profileStep === 1 && (
            <>
              <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>{t("getStarted.profileStep1Description")}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("auth.fullName")} *</label>
                  <Input placeholder={t("auth.fullNamePlaceholder")} value={profileFullName} onChange={(e) => setProfileFullName(e.target.value)} />
                </div>
                {user?.email && (
                  <div>
                    <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("auth.emailAddress")}</label>
                    <Input value={user.email} disabled style={{ background: "#f3f4f6", color: "#6b7280" }} />
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6b7280" }}>{t("getStarted.contactSupportToChangeEmail")}</p>
                  </div>
                )}
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>Phone</label>
                  <Input placeholder="+1 234 567 8900" type="tel" value={profilePhone} onChange={(e) => setProfilePhone(e.target.value)} />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>Job Title</label>
                  <Input placeholder={t("getStarted.jobTitlePlaceholder")} value={profileJobTitle} onChange={(e) => setProfileJobTitle(e.target.value)} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.timezone")}</label>
                    <select value={profileTimezone} onChange={(e) => setProfileTimezone(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}>
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern</option>
                      <option value="America/Chicago">Central</option>
                      <option value="America/Denver">Mountain</option>
                      <option value="America/Los_Angeles">Pacific</option>
                      <option value="Europe/London">London</option>
                      <option value="Europe/Paris">Paris</option>
                      <option value="Asia/Tokyo">Tokyo</option>
                      <option value="Asia/Kolkata">India</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.language")}</label>
                    <select value={profileLanguage} onChange={(e) => setProfileLanguage(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}>
                      <option value="en">{t("getStarted.english")}</option>
                      <option value="es">{t("getStarted.spanish")}</option>
                      <option value="fr">{t("getStarted.french")}</option>
                      <option value="de">{t("getStarted.german")}</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}

          {profileStep === 2 && (
            <>
              <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>{t("getStarted.profileStep2Description")}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.businessCompanyName")}</label>
                  <Input placeholder={t("getStarted.businessPlaceholder")} value={profileBusinessName} onChange={(e) => setProfileBusinessName(e.target.value)} />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.logoUrl")}</label>
                  <Input placeholder="https://example.com/logo.png" type="url" value={profileLogoUrl} onChange={(e) => setProfileLogoUrl(e.target.value)} />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.companyWebsite")}</label>
                  <Input placeholder="https://mycompany.com" type="url" value={profileCompanyWebsite} onChange={(e) => setProfileCompanyWebsite(e.target.value)} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.industry")}</label>
                    <Input placeholder={t("getStarted.industryPlaceholder")} value={profileIndustry} onChange={(e) => setProfileIndustry(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.companySize")}</label>
                    <select value={profileCompanySize} onChange={(e) => setProfileCompanySize(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}>
                      <option value="">Select</option>
                      <option value="1-10">1–10</option>
                      <option value="11-50">11–50</option>
                      <option value="51-200">51–200</option>
                      <option value="201-500">201–500</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.currency")}</label>
                  <select value={profileCurrency} onChange={(e) => setProfileCurrency(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="NGN">NGN</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {profileStep === 3 && (
            <>
              <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>{t("getStarted.profileStep3Description")}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.streetAddress")}</label>
                  <Input placeholder={t("getStarted.streetPlaceholder")} value={profileAddressStreet} onChange={(e) => setProfileAddressStreet(e.target.value)} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.city")}</label>
                    <Input placeholder={t("getStarted.city")} value={profileAddressCity} onChange={(e) => setProfileAddressCity(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.stateProvince")}</label>
                    <Input placeholder={t("getStarted.stateProvince")} value={profileAddressState} onChange={(e) => setProfileAddressState(e.target.value)} />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.country")}</label>
                    <Input placeholder={t("getStarted.country")} value={profileAddressCountry} onChange={(e) => setProfileAddressCountry(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>{t("getStarted.postalCode")}</label>
                    <Input placeholder={t("getStarted.postalCode")} value={profileAddressPostalCode} onChange={(e) => setProfileAddressPostalCode(e.target.value)} />
                  </div>
                </div>
              </div>
            </>
          )}

          {profileError && (
            <div style={{ padding: 12, background: "#fee2e2", borderRadius: 8, color: "#991b1b", fontSize: 14 }}>
              {profileError}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8, borderTop: "1px solid #e5e7eb" }}>
            <div>
              {profileStep > 1 ? (
                <Button type="button" $variant="outline" onClick={() => { setProfileStep(profileStep - 1); setProfileError(null); }}>
                  <ChevronLeft size={16} />
                  {t("common.back")}
                </Button>
              ) : (
                <Button type="button" $variant="outline" onClick={handleCloseProfileModal}>{t("common.cancel")}</Button>
              )}
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              {profileStep < 3 ? (
                <Button
                  type="button"
                  onClick={() => {
                    if (profileStep === 1 && !profileFullName.trim()) {
                      setProfileError(t("getStarted.fullNameRequired"));
                      return;
                    }
                    setProfileError(null);
                    setProfileStep(profileStep + 1);
                  }}
                >
                  {t("common.next")} <ChevronRight size={16} />
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={profileSubmitting}
                  onClick={handleSubmitProfileModal}
                >
                  {profileSubmitting ? t("getStarted.saving") : t("getStarted.saveProfile")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Invite Modal */}
      <Modal
        open={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        title={t("getStarted.inviteTeamMember")}
        maxWidth="520px"
      >
        {!generatedInviteUrl ? (
          <form onSubmit={handleSubmitInviteModal} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                {t("getStarted.roleRequired")}
              </label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                style={{ width: "100%", padding: "12px 16px", borderRadius: 12, border: "1px solid #d1d5db", fontSize: 16 }}
                required
              >
                <option value="user">{t("getStarted.roleUser")}</option>
                <option value="manager">{t("getStarted.roleManager")}</option>
                <option value="admin">{t("getStarted.roleAdmin")}</option>
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                {t("getStarted.emailOptional")}
              </label>
              <Input
                placeholder={t("auth.emailPlaceholder")}
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6b7280" }}>
                {t("getStarted.inviteEmailHint")}
              </p>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                {t("getStarted.messageOptional")}
              </label>
              <textarea
                placeholder={t("getStarted.optionalPersonalMessage")}
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                style={{
                  width: "100%",
                  minHeight: 80,
                  padding: "12px 16px",
                  borderRadius: 12,
                  border: "1px solid #d1d5db",
                  fontSize: 16,
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
              />
            </div>
            {inviteError && (
              <div style={{ padding: 12, background: "#fee2e2", borderRadius: 8, color: "#991b1b", fontSize: 14 }}>
                {inviteError}
              </div>
            )}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <Button type="button" $variant="outline" onClick={handleCloseInviteModal}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={inviteSubmitting}>
                {inviteSubmitting ? t("getStarted.creating") : t("getStarted.createInviteLink")}
              </Button>
            </div>
          </form>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ padding: 16, background: "#dcfce7", borderRadius: 12 }}>
              <p style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 600, color: "#166534" }}>
                {t("getStarted.inviteLinkCreated")}
              </p>
              <p style={{ margin: 0, fontSize: 13, color: "#166534" }}>
                {t("getStarted.copyLinkBelow")}
              </p>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                {t("getStarted.inviteLink")}
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <Input
                  value={generatedInviteUrl}
                  readOnly
                  style={{ flex: 1 }}
                />
                <Button onClick={handleCopyInviteUrl} $variant="outline">
                  <Copy size={16} />
                </Button>
              </div>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <Button onClick={handleCloseInviteModal}>
                {t("common.done")}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </Container>
  );
}

